// frontend/src/lib/hasPermission.ts — the frontend mirror of the backend
// capability primitive (`backend/src/core/auth/hasPermission.ts`).
//
// Answers one question: "does this principal HOLD capability `key` in this
// project?" The `/me` permissions array is already includes-expanded by the
// backend (ADR-025 — `write` ⊇ `read` resolved once in the permission cache),
// so this is a membership lookup behind two short-circuits — identical shape to
// the backend's single Set lookup. Sysadmin / project_admin see everything
// (ADR-016 / ADR-022).
//
// This is NOT the authorization decision (that is the backend `can()` —
// capability + scope, server-side). This mirror only gates render: which nav
// items, buttons, and menu entries a user can SEE. The backend re-validates
// every write, so a stale or spoofed mirror never grants real access.
//
// The catalogue + the includes-expansion live in @ems-app/schemas/permissions,
// shared backend↔frontend per ADR-009 — never hand-author a key list.

import type { ProjectPermissions } from '@ems-app/schemas/auth/responses';

/**
 * @param perms       the per-project entry from `me.permissions[projectId]`
 *                    (undefined when the user is not a member of the project)
 * @param isSysadmin  `me.user.is_sysadmin` (user-level, not per-project)
 * @param key         a capability key from the catalogue
 */
export function hasPermission(
  perms: ProjectPermissions | undefined,
  isSysadmin: boolean,
  key: string,
): boolean {
  if (isSysadmin) return true;
  if (!perms) return false; // not a member of this project
  if (perms.is_project_admin) return true;
  return perms.permissions.includes(key);
}
